// Fonte única do catálogo de planos no frontend (P23C): consome GET
// /billing/plans em vez de arrays de planos/preços/recursos hardcoded em
// cada página. Também computa o que se ganha/perde ao trocar de plano, a
// partir dos entitlements crus retornados pela API — nunca de uma cópia
// paralela de regras de negócio no frontend.
import { apiClient } from './http.js';

export const PLAN_RANK = { trial: 0, basic: 1, regular: 2, premium: 3 };

export async function fetchPlanCatalog() {
  const { res, data } = await apiClient.request('/billing/plans');
  if (!res.ok || !Array.isArray(data?.plans)) return [];
  return [...data.plans].sort((a, b) => a.rank - b.rank);
}

const SUPPORT_RANK = { help_center: 0, email: 1, priority: 2, priority_sla: 3 };
const SUPPORT_LABEL_PT = {
  help_center: 'Central de ajuda',
  email: 'Suporte por e-mail',
  priority: 'Suporte prioritário',
  priority_sla: 'Suporte prioritário com SLA',
};
const PERSONA_LABEL_PT = {
  padre_amigo: 'Padre Amigo',
  teologo: 'Teólogo',
  catequista: 'Catequista',
  diretor: 'Diretor Espiritual',
};

/**
 * Compara os entitlements de dois planos e retorna frases prontas do que se
 * ganha e do que se perde ao trocar de `from` para `to` — usado para avisar
 * o usuário antes de confirmar upgrade/downgrade (P23C: "Upgrade informa
 * ganho funcional" / "Downgrade informa perdas").
 */
export function diffPlanEntitlements(fromPlan, toPlan) {
  const gains = [];
  const losses = [];
  if (!fromPlan || !toPlan) return { gains, losses };

  const from = fromPlan.entitlements;
  const to = toPlan.entitlements;

  const fromCredits = fromPlan.credits_per_week ?? 0;
  const toCredits = toPlan.credits_per_week ?? 0;
  if (toCredits > fromCredits) gains.push(`${toCredits} mensagens por semana (antes ${fromCredits})`);
  if (toCredits < fromCredits) losses.push(`${toCredits} mensagens por semana (antes ${fromCredits})`);

  const fromPersonas = new Set(from.chat_personas);
  const toPersonas = new Set(to.chat_personas);
  const newPersonas = to.chat_personas.filter((p) => !fromPersonas.has(p));
  const lostPersonas = from.chat_personas.filter((p) => !toPersonas.has(p));
  if (newPersonas.length) gains.push(`Novas personas de IA: ${newPersonas.map((p) => PERSONA_LABEL_PT[p] || p).join(', ')}`);
  if (lostPersonas.length) losses.push(`Personas que deixam de estar disponíveis: ${lostPersonas.map((p) => PERSONA_LABEL_PT[p] || p).join(', ')}`);

  if (to.synod_enabled && !from.synod_enabled) gains.push('Acesso ao Sínodo Virtual');
  if (!to.synod_enabled && from.synod_enabled) losses.push('Acesso ao Sínodo Virtual');

  const readingGain = _compareLimit(from.reading_active_plan_limit, to.reading_active_plan_limit);
  if (readingGain === 'up') gains.push('Mais planos de Leitura Assistida ativos ao mesmo tempo');
  if (readingGain === 'down') losses.push('Menos planos de Leitura Assistida ativos ao mesmo tempo');
  if (to.reading_deepen_enabled && !from.reading_deepen_enabled) gains.push('"Aprofundar com IA" na Leitura Assistida');
  if (!to.reading_deepen_enabled && from.reading_deepen_enabled) losses.push('"Aprofundar com IA" na Leitura Assistida');

  const notesGain = _compareLimit(from.notes_max_count, to.notes_max_count);
  if (notesGain === 'up') gains.push('Mais anotações no Caderno Espiritual');
  if (notesGain === 'down') losses.push('Menos anotações permitidas no Caderno Espiritual (notas já salvas continuam acessíveis e exportáveis)');
  if (to.notes_semantic_search && !from.notes_semantic_search) gains.push('Busca semântica no Caderno Espiritual');
  if (!to.notes_semantic_search && from.notes_semantic_search) losses.push('Busca semântica no Caderno Espiritual');
  if (to.notes_export && !from.notes_export) gains.push('Exportação do Caderno Espiritual');
  if (!to.notes_export && from.notes_export) losses.push('Exportação do Caderno Espiritual');

  const trailsGain = _compareLimit(from.trails_active_limit, to.trails_active_limit);
  if (trailsGain === 'up') gains.push('Mais trilhas de estudo ativas ao mesmo tempo');
  if (trailsGain === 'down') losses.push('Menos trilhas de estudo ativas ao mesmo tempo');

  if (to.insights_history_days > from.insights_history_days) gains.push('Histórico de Insights mais longo');
  if (to.insights_history_days < from.insights_history_days) losses.push('Histórico de Insights mais curto');

  const fromSupport = SUPPORT_RANK[from.support_level] ?? 0;
  const toSupport = SUPPORT_RANK[to.support_level] ?? 0;
  if (toSupport > fromSupport) gains.push(SUPPORT_LABEL_PT[to.support_level] || 'Suporte melhorado');
  if (toSupport < fromSupport) losses.push(`${SUPPORT_LABEL_PT[from.support_level]} passa a ser ${SUPPORT_LABEL_PT[to.support_level]}`);

  return { gains, losses };
}

// null = sem limite (melhor que qualquer número finito).
function _compareLimit(fromLimit, toLimit) {
  const fromVal = fromLimit === null ? Infinity : fromLimit;
  const toVal = toLimit === null ? Infinity : toLimit;
  if (toVal > fromVal) return 'up';
  if (toVal < fromVal) return 'down';
  return 'same';
}
